import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    Inject,
    Input,
    OnChanges,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Sitecore, TeaserBetConfig } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { Store } from '@ngrx/store';
import { Subject, debounceTime } from 'rxjs';

import { HandleType } from '../../../../../lib/common/slider/model';
import { SliderComponent } from '../../../../../lib/common/slider/slider.component';
import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../base/store/state';
import { IBetslipInfoMessage } from '../../../common/models';
import { InfoMessagesActions } from '../../../info-messages/actions';
import { InfoTrackingContext } from '../../../tracking/models';
import LinearBetslipTrackingActions from '../../../tracking/state/linear-tracking.actions';
import { BetslipTeaserActions } from '../../actions';
import { BetslipTeaserTrackingData, TrackingEvent } from '../../models';
import { TeaserBetTrackingActions } from '../../teaser-bet-tracking.actions';

@Component({
    selector: 'bs-teaser-bet-point-selector',
    templateUrl: './teaser-bet-point-selector.component.html',
    styleUrls: ['./teaser-bet-point-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class TeaserBetPointSelectorComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() steps: number[];
    @Input() sliderValue: number;
    @Input() stepValue: number;
    @Input() stepDivider: number;
    @Input() isLinear: boolean;

    @ViewChild(SliderComponent, { static: true }) sliderComponent: SliderComponent;

    teasedValue: string;

    betslipMessages!: IBetslipMessages;
    readonly sliderStepId = 'teaser-';
    readonly teaserBetPointMessageId = 'teaserBetPointMessageInfo';
    betslipInfoMessage: IBetslipInfoMessage;
    private stepDifference: number;
    private sliderValue$ = new Subject<number>();
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private sitecore: Sitecore,
        private teaserBetConfig: TeaserBetConfig,
        private store: Store<IBetslipRootState>,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private timerService: TimerService,
    ) {
        this.betslipMessages = messageProvider.getMessages();
        this.betslipInfoMessage = {
            header: this.betslipMessages.teaserBet?.messages.TeaserInfoHeader!,
            infoText: this.betslipMessages.teaserBet?.messages.TeaserInfoText!,
            closeText: this.betslipMessages.common.BankerHelpClose,
        };
    }

    ngOnInit(): void {
        this.sliderValue$.pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.store.dispatch(BetslipTeaserActions.setTeasePicks({ keypoint: value }));
        });
    }

    ngOnChanges(changes: ISimpleChanges<TeaserBetPointSelectorComponent>): void {
        if (changes.sliderValue?.isFirstChange()) {
            this.store.dispatch(BetslipTeaserActions.setTeasePicks({ keypoint: this.sliderValue }));
        }
        this.teasedValue = interpolateDoubleBracesString(this.sitecore.betslip.teaserBet.messages.SliderText, {
            teasedPoint: this.sliderValue,
        });

        if (
            !changes.stepDivider?.isFirstChange() &&
            changes.stepDivider?.currentValue !== changes.stepDivider?.previousValue &&
            this.sliderComponent
        ) {
            this.sliderComponent.stepDivider = this.stepDivider;
        }

        if (!changes.steps?.isFirstChange() && changes.steps?.currentValue !== changes.steps?.previousValue) {
            this.stepDifference = changes.steps?.previousValue?.length! - changes.steps?.currentValue?.length!;
            if (this.stepDifference !== 0) {
                this.timerService.setTimeout(() => {
                    this.sliderComponent.updateHandler();
                    this.stepDifference = 0;
                }, 500);
            }
        }
    }

    ngAfterViewInit(): void {
        this.sliderComponent.stepDivider = this.stepDivider;
    }

    onPointChange(sliderValue: number): void {
        this.sliderValue$.next(sliderValue);
    }

    teasePoints(increment: boolean): void {
        const stepValue = this.stepValue ?? this.teaserBetConfig.pointsSliderConfig[0].stepValue;
        this.sliderComponent.sliderExternalAction(HandleType.MinHandle, increment ? stepValue : -stepValue, this.stepDivider);
        this.trackingSelector(increment);
    }

    closeInfoBox() {
        this.store.dispatch(InfoMessagesActions.closeById({ messageId: this.teaserBetPointMessageId }));
    }

    showTeaserInfo(): void {
        this.store.dispatch(InfoMessagesActions.toggle({ messageId: this.teaserBetPointMessageId }));
        this.trackingSelectorInfoIcon();
    }

    trackingSelector(increment: boolean): void {
        const trackingData: BetslipTeaserTrackingData = {
            action: TrackingEvent.Click,
            eventDetails: increment ? '+' : '-',
            isLinear: this.isLinear,
        };
        this.store.dispatch(TeaserBetTrackingActions.trackTeaserPointsSelection({ payload: trackingData }));
    }

    trackingSelectorInfoIcon(): void {
        if (!this.isLinear) {
            const trackingData: BetslipTeaserTrackingData = {
                action: TrackingEvent.Click,
                eventDetails: 'teaser info icon',
                isLinear: this.isLinear,
            };
            this.store.dispatch(TeaserBetTrackingActions.trackTeaserPointsInfoSelection({ payload: trackingData }));

            return;
        }

        this.store.dispatch(
            LinearBetslipTrackingActions.clickInfoIcon({ messageId: this.teaserBetPointMessageId, infoContext: InfoTrackingContext.TeaserBet }),
        );
    }
}
